import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { auth } from '../../../firebase/config';
import { db } from '../../../firebase/config';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiEdit2, FiUsers } from 'react-icons/fi';
import Icon from '../../../components/Icon';
import Table, { Column } from '../../../components/Table';
import LocationFormModal from '../components/LocationFormModal';
import {
    fetchRegionLocations,
    createRegionLocation,
    updateRegionLocation,
    type RegionLocation,
} from '../service/locationsService';
import { useAppSelector } from '../../../store';
import { isPsoHeadDesignation } from '../../../utils/psoDesignation';
import { fetchBranchesData, type Branch } from '../service/branchesService';
import Modal from '../../../components/Modal';
import { doc, updateDoc, deleteField, deleteDoc } from 'firebase/firestore';
import { fetchPSOs, type PSO } from '../../pso/service/psoService';

export default function Locations() {
    const [rows, setRows] = useState<RegionLocation[]>([]);
    const [filtered, setFiltered] = useState<RegionLocation[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [psos, setPsos] = useState<PSO[]>([]);
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
        show: false,
        message: '',
        type: 'success',
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit' | 'view'>('add');
    const [selected, setSelected] = useState<RegionLocation | null>(null);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [assignLocation, setAssignLocation] = useState<RegionLocation | null>(null);
    const [assignSubmitting, setAssignSubmitting] = useState(false);
    const [assignmentDraft, setAssignmentDraft] = useState<Record<string, string>>({});
    const [assignPsoFilter, setAssignPsoFilter] = useState<string>('all'); // 'all' | 'unassigned' | '<id>|<legacyId?>'
    const [assignSearch, setAssignSearch] = useState<string>('');
    const [bulkAssignPsoId, setBulkAssignPsoId] = useState<string>('');
    const [bulkPreviewOpen, setBulkPreviewOpen] = useState(false);
    const [bulkBranchesToUpdate, setBulkBranchesToUpdate] = useState<Branch[]>([]);
    const [bulkSubmitting, setBulkSubmitting] = useState(false);
    const [branchDeleteOpen, setBranchDeleteOpen] = useState(false);
    const [branchToDelete, setBranchToDelete] = useState<Branch | null>(null);
    const [branchDeleteSubmitting, setBranchDeleteSubmitting] = useState(false);
    const [deleteLocOpen, setDeleteLocOpen] = useState(false);
    const [locToDelete, setLocToDelete] = useState<RegionLocation | null>(null);
    const [deleteLocSubmitting, setDeleteLocSubmitting] = useState(false);

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';
    const psoProfile = useAppSelector((s) => s.user.pso);
    const isHeadReadOnly = !isBankUser && isPsoHeadDesignation(psoProfile?.designation);

    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ show: true, message, type });
    };

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const list = await fetchRegionLocations();
            setRows(list);
            setFiltered(list);

            // Also load branches so we can show KPI + PSO assignment per location.
            if (uid) {
                const { branches: b } = await fetchBranchesData(
                    uid,
                    isBankUser,
                    uid,
                    psoProfile?.designation
                );
                setBranches(b);

                // Bank-only: load PSOs list for assignment dropdowns (needs a bankId).
                if (isBankUser) {
                    const bankId = b[0]?.bankId;
                    if (bankId) {
                        const p = await fetchPSOs(bankId);
                        setPsos(p);
                    } else {
                        setPsos([]);
                    }
                } else {
                    setPsos([]);
                }
            } else {
                setBranches([]);
                setPsos([]);
            }
        } catch (e) {
            console.error(e);
            setToast({ show: true, message: 'Failed to load locations', type: 'error' });
        } finally {
            if (!silent) setLoading(false);
        }
    }, [uid, isBankUser, psoProfile?.designation]);

    useEffect(() => {
        if (!uid) return;
        void load(false);
    }, [uid, load]);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFiltered(rows);
            setCurrentPage(1);
            return;
        }
        const t = searchTerm.toLowerCase();
        const next = rows.filter((r) => {
            const name = (r.name || '').toLowerCase();
            const addr = (r.headOfficeAddress || '').toLowerCase();
            return name.includes(t) || addr.includes(t);
        });
        setFiltered(next);
        setCurrentPage(1);
    }, [searchTerm, rows]);

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = useMemo(
        () => filtered.slice((currentPage - 1) * perPage, currentPage * perPage),
        [filtered, currentPage]
    );

    const locationStats = useMemo(() => {
        const totalLocations = rows.length;
        const activeBranches = branches.filter((b) => b.isActive !== false);
        const branchCount = activeBranches.length;
        const branchesByLocation = new Map<string, Branch[]>();
        for (const b of activeBranches) {
            const key = String(b.locationId || '');
            if (!key) continue;
            const arr = branchesByLocation.get(key) ?? [];
            arr.push(b);
            branchesByLocation.set(key, arr);
        }
        const locationsWithBranches = Array.from(branchesByLocation.keys()).length;

        const uniquePsoIds = new Set<string>();
        for (const b of activeBranches) {
            if (b.psoId) uniquePsoIds.add(String(b.psoId));
        }

        return {
            totalLocations,
            branchCount,
            locationsWithBranches,
            uniquePsoCount: uniquePsoIds.size,
            branchesByLocation,
            allBranchesByLocation: (() => {
                const m = new Map<string, Branch[]>();
                for (const b of branches) {
                    const key = String(b.locationId || '');
                    if (!key) continue;
                    const arr = m.get(key) ?? [];
                    arr.push(b);
                    m.set(key, arr);
                }
                return m;
            })(),
        };
    }, [rows, branches]);

    const openAdd = () => {
        setSelected(null);
        setModalMode('add');
        setModalOpen(true);
    };

    const openView = (loc: RegionLocation) => {
        setSelected(loc);
        setModalMode('view');
        setModalOpen(true);
    };

    const openEdit = (loc: RegionLocation) => {
        setSelected(loc);
        setModalMode('edit');
        setModalOpen(true);
    };

    const branchesForLocation = useMemo(() => {
        if (!assignLocation) return [];
        const list = locationStats.allBranchesByLocation.get(assignLocation.id) ?? [];
        return [...list].sort((a, b) => {
            const aActive = a.isActive !== false;
            const bActive = b.isActive !== false;
            if (aActive !== bActive) return aActive ? -1 : 1;
            return String(a.branchCode || '').localeCompare(String(b.branchCode || ''));
        });
    }, [assignLocation, locationStats.allBranchesByLocation]);

    const openAssign = (loc: RegionLocation) => {
        if (!isBankUser) return;
        setAssignLocation(loc);
        const list = locationStats.allBranchesByLocation.get(loc.id) ?? [];
        const nextDraft: Record<string, string> = {};
        list.forEach((b) => {
            nextDraft[b.id] = b.psoId ? String(b.psoId) : '';
        });
        setAssignmentDraft(nextDraft);
        setAssignPsoFilter('all');
        setAssignSearch('');
        setBulkAssignPsoId('');
        setBulkPreviewOpen(false);
        setBulkBranchesToUpdate([]);
        setAssignModalOpen(true);
    };

    const saveAssignments = async () => {
        if (!isBankUser || !assignLocation) return;
        const list = locationStats.allBranchesByLocation.get(assignLocation.id) ?? [];
        const bankId = list[0]?.bankId;
        if (!bankId) {
            showToast('No bank found for these branches', 'error');
            return;
        }

        setAssignSubmitting(true);
        try {
            const updates: Promise<void>[] = [];
            for (const b of list) {
                const next = assignmentDraft[b.id] ?? '';
                const prev = b.psoId ? String(b.psoId) : '';
                if (next === prev) continue;
                const ref = doc(db, 'banks', bankId, 'branch', b.id);
                if (!next) {
                    updates.push(updateDoc(ref, { psoId: deleteField() } as any));
                } else {
                    updates.push(updateDoc(ref, { psoId: next }));
                }
            }
            await Promise.all(updates);
            showToast('PSO assignment updated', 'success');
            setAssignModalOpen(false);
            setAssignLocation(null);
            await load(true);
        } catch (e) {
            console.error(e);
            showToast('Failed to update assignments', 'error');
        } finally {
            setAssignSubmitting(false);
        }
    };

    const assignPsoFilterIds = useMemo(() => {
        if (!assignPsoFilter || assignPsoFilter === 'all') return null;
        if (assignPsoFilter === 'unassigned') return new Set<string>(['__unassigned__']);
        const parts = String(assignPsoFilter).split('|').map((x) => x.trim()).filter(Boolean);
        return new Set<string>(parts);
    }, [assignPsoFilter]);

    const psosInSelectedLocation = useMemo(() => {
        // Only show PSOs that are currently present in this location's branches.
        // We match both PSO Firestore doc id and legacy (pso as any).psoId.
        const ids = new Set<string>();
        for (const b of branchesForLocation) {
            if (b.psoId) ids.add(String(b.psoId));
        }

        const opts = psos
            .map((p) => {
                const legacy = (p as any).psoId ? String((p as any).psoId) : '';
                const value = legacy && legacy !== p.id ? `${p.id}|${legacy}` : `${p.id}`;
                const matches = ids.has(String(p.id)) || (legacy ? ids.has(legacy) : false);
                return matches
                    ? {
                          id: p.id,
                          label: p.name || '—',
                          value,
                      }
                    : null;
            })
            .filter(Boolean) as { id: string; label: string; value: string }[];

        opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        return opts;
    }, [branchesForLocation, psos]);

    const filteredBranchesForAssignModal = useMemo(() => {
        const list = branchesForLocation;
        const search = assignSearch.trim().toLowerCase();

        return list.filter((b) => {
            // PSO dropdown filter
            if (assignPsoFilterIds) {
                if (assignPsoFilter === 'unassigned') {
                    if (b.psoId) return false;
                } else {
                    const pid = b.psoId ? String(b.psoId) : '';
                    if (!pid || !assignPsoFilterIds.has(pid)) return false;
                }
            }

            // Search filter: branch code, address/name, current PSO name
            if (search) {
                const branchCode = String(b.branchCode || '').toLowerCase();
                const branchName = String((b as any).branchName || b.address || '').toLowerCase();
                const psoName = String(b.psoName || '').toLowerCase();
                if (!branchCode.includes(search) && !branchName.includes(search) && !psoName.includes(search)) {
                    return false;
                }
            }

            return true;
        });
    }, [branchesForLocation, assignSearch, assignPsoFilter, assignPsoFilterIds]);

    const setBranchActive = async (branch: Branch, nextActive: boolean) => {
        if (!isBankUser) return;
        if (!branch.bankId) return;
        setAssignSubmitting(true);
        try {
            await updateDoc(doc(db, 'banks', branch.bankId, 'branch', branch.id), {
                isActive: nextActive,
                updatedAt: new Date(),
            } as any);
            showToast(nextActive ? 'Branch activated' : 'Branch inactivated', 'success');
            await load(true);
        } catch (e) {
            console.error(e);
            showToast('Failed to update branch status', 'error');
        } finally {
            setAssignSubmitting(false);
        }
    };

    const shiftBranchRegion = async (branch: Branch, nextLocationId: string) => {
        if (!isBankUser) return;
        if (!branch.bankId) return;
        const next = String(nextLocationId || '').trim();
        if (!next || next === String(branch.locationId || '')) return;
        setAssignSubmitting(true);
        try {
            await updateDoc(doc(db, 'banks', branch.bankId, 'branch', branch.id), {
                locationId: next,
                updatedAt: new Date(),
            } as any);
            showToast('Branch shifted to another region', 'success');
            // Close modal because the branch will disappear from this region.
            setAssignModalOpen(false);
            setAssignLocation(null);
            await load(true);
        } catch (e) {
            console.error(e);
            showToast('Failed to shift branch region', 'error');
        } finally {
            setAssignSubmitting(false);
        }
    };

    const openDeleteBranch = (branch: Branch) => {
        if (!isBankUser) return;
        setBranchToDelete(branch);
        setBranchDeleteOpen(true);
    };

    const confirmDeleteBranch = async () => {
        if (!isBankUser || !branchToDelete) return;
        if (!branchToDelete.bankId) return;
        setBranchDeleteSubmitting(true);
        try {
            await deleteDoc(doc(db, 'banks', branchToDelete.bankId, 'branch', branchToDelete.id));
            showToast('Branch deleted permanently', 'success');
            setBranchDeleteOpen(false);
            setBranchToDelete(null);
            await load(true);
        } catch (e) {
            console.error(e);
            showToast('Failed to delete branch', 'error');
        } finally {
            setBranchDeleteSubmitting(false);
        }
    };

    const openDeleteLocation = (loc: RegionLocation) => {
        if (!isBankUser) return;
        setLocToDelete(loc);
        setDeleteLocOpen(true);
    };

    const confirmDeleteLocation = async () => {
        if (!isBankUser || !locToDelete) return;
        const branchCount = locationStats.branchesByLocation.get(locToDelete.id)?.length ?? 0;
        if (branchCount > 0) {
            showToast('Cannot delete this location while branches exist in it. Move branches to another location first.', 'error');
            return;
        }

        setDeleteLocSubmitting(true);
        try {
            await deleteDoc(doc(db, 'locations', locToDelete.id));
            showToast('Location deleted', 'success');
            setDeleteLocOpen(false);
            setLocToDelete(null);
            await load(true);
        } catch (e) {
            console.error(e);
            showToast('Failed to delete location', 'error');
        } finally {
            setDeleteLocSubmitting(false);
        }
    };

    const openBulkPreview = () => {
        if (!isBankUser || !assignLocation) return;
        if (!bulkAssignPsoId) {
            showToast('Select a PSO to assign', 'error');
            return;
        }
        const list = locationStats.allBranchesByLocation.get(assignLocation.id) ?? [];
        const target = String(bulkAssignPsoId);

        // Only update branches that are NOT already assigned to this PSO.
        const toUpdate = list.filter((b) => String(b.psoId || '') !== target);
        setBulkBranchesToUpdate(toUpdate);
        setBulkPreviewOpen(true);
    };

    const confirmBulkAssign = async () => {
        if (!isBankUser || !assignLocation) return;
        if (!bulkAssignPsoId) return;
        const list = locationStats.allBranchesByLocation.get(assignLocation.id) ?? [];
        const bankId = list[0]?.bankId;
        if (!bankId) return;

        const target = String(bulkAssignPsoId);
        setBulkSubmitting(true);
        try {
            const updates = bulkBranchesToUpdate.map((b) =>
                updateDoc(doc(db, 'banks', bankId, 'branch', b.id), { psoId: target } as any)
            );
            await Promise.all(updates);
            showToast('Assigned PSO to region branches', 'success');
            setBulkPreviewOpen(false);
            setBulkBranchesToUpdate([]);
            await load(true);
        } catch (e) {
            console.error(e);
            showToast('Failed to assign PSO to region', 'error');
        } finally {
            setBulkSubmitting(false);
        }
    };

    const handleSave = async (payload: {
        name: string;
        headOfficeAddress: string;
        latitude: string;
        longitude: string;
    }) => {
        if (modalMode === 'add') {
            await createRegionLocation(payload);
            showToast('Location added');
        } else if (modalMode === 'edit' && selected) {
            await updateRegionLocation(selected.id, payload);
            showToast('Location updated');
        }
        await load(true);
    };

    const columns: Column<RegionLocation>[] = [
        {
            header: 'Region name',
            accessor: 'name',
            width: '22%',
            render: (r) => <span className="font-medium text-gray-900 dark:text-gray-100">{r.name || '—'}</span>,
        },
        {
            header: 'Branches',
            width: '10%',
            align: 'center',
            render: (r) => {
                const count = locationStats.branchesByLocation.get(r.id)?.length ?? 0;
                return <span className="tabular-nums text-sm text-gray-600 dark:text-gray-400">{count}</span>;
            },
        },
        {
            header: 'Assigned PSOs',
            width: '20%',
            render: (r) => {
                const list = locationStats.branchesByLocation.get(r.id) ?? [];
                const uniqueNames: string[] = [];
                const seen = new Set<string>();
                for (const b of list) {
                    const name = (b.psoName || '').trim();
                    if (!name) continue;
                    const key = name.toLowerCase();
                    if (seen.has(key)) continue;
                    seen.add(key);
                    uniqueNames.push(name);
                }
                uniqueNames.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
                if (uniqueNames.length === 0) {
                    return <span className="text-sm text-gray-500 dark:text-gray-400">—</span>;
                }
                return (
                    <div className="flex flex-wrap gap-1">
                        {uniqueNames.slice(0, 3).map((n) => (
                            <span
                                key={n}
                                className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                                title={n}
                            >
                                {n}
                            </span>
                        ))}
                        {uniqueNames.length > 3 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">+{uniqueNames.length - 3}</span>
                        )}
                    </div>
                );
            },
        },
        {
            header: 'Head office address',
            width: '38%',
            render: (r) => (
                <div
                    style={{
                        maxWidth: '320px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                    className="text-sm text-gray-600 dark:text-gray-400"
                >
                    {r.headOfficeAddress || '—'}
                </div>
            ),
        },
        {
            header: 'Coordinates',
            width: '20%',
            render: (r) => {
                const lat = r.location?.lat;
                const long = r.location?.long;
                if (lat == null || long == null) {
                    return <span className="text-gray-500 dark:text-gray-400">—</span>;
                }
                return (
                    <span className="tabular-nums text-sm text-gray-600 dark:text-gray-400">
                        {Number(lat).toFixed(5)}, {Number(long).toFixed(5)}
                    </span>
                );
            },
        },
        {
            header: 'Action',
            width: '12%',
            render: (r) => (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => openView(r)}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                        title="View"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    {isBankUser && (
                        <button
                            type="button"
                            onClick={() => openAssign(r)}
                            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                            title="Assign / Unassign PSO"
                        >
                            <Icon icon={FiUsers} className="h-4 w-4" />
                        </button>
                    )}
                    {isBankUser && (
                        <button
                            type="button"
                            onClick={() => openEdit(r)}
                            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                            title="Edit"
                        >
                            <Icon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                    )}
                    {isBankUser && (
                        <button
                            type="button"
                            onClick={() => openDeleteLocation(r)}
                            className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-white px-2 py-1.5 text-xs font-medium text-red-700 transition-all hover:border-red-400 hover:bg-red-50 hover:text-red-700 dark:border-red-900/50 dark:bg-gray-800 dark:text-red-400 dark:hover:border-red-500 dark:hover:bg-red-950/30"
                            title="Delete location"
                        >
                            <span className="sr-only">Delete</span>
                            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18" />
                                <path d="M8 6V4h8v2" />
                                <path d="M6 6l1 16h10l1-16" />
                                <path d="M10 11v6" />
                                <path d="M14 11v6" />
                            </svg>
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="space-y-8">
                <div className="border-b border-gray-200 pb-6 dark:border-gray-800">
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Locations</h1>
                    <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
                        {isBankUser
                            ? 'Manage region locations used when creating branches: name, head office address, and map coordinates.'
                            : isHeadReadOnly
                              ? 'View region locations (read-only). Add and edit are available to bank users only.'
                              : 'View region locations linked to branches.'}
                    </p>
                    {isHeadReadOnly && (
                        <p className="mt-3 max-w-2xl rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100">
                            Read-only access for Head. You can open a location to view details; you cannot add or edit.
                        </p>
                    )}
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-black/30">
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Total locations</div>
                        <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100">{locationStats.totalLocations}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-black/30">
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Total branches</div>
                        <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100">{locationStats.branchCount}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-black/30">
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Locations with branches</div>
                        <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100">{locationStats.locationsWithBranches}</div>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-black/30">
                        <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">Assigned PSOs</div>
                        <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100">{locationStats.uniquePsoCount}</div>
                    </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="relative max-w-md flex-1">
                        <Icon
                            icon={FiSearch}
                            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        />
                        <input
                            type="search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by name or address…"
                            className="form-control w-full pl-9"
                        />
                    </div>
                </div>

                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage={searchTerm ? 'No locations match your search' : 'No locations yet'}
                    pagination={{
                        currentPage,
                        totalPages: Math.max(1, totalPages),
                        onPageChange: setCurrentPage,
                    }}
                    headerActions={
                        isBankUser ? (
                            <Button type="button" onClick={openAdd} className="inline-flex items-center gap-2 !py-2">
                                <Icon icon={FiPlus} className="h-4 w-4" />
                                Add location
                            </Button>
                        ) : undefined
                    }
                />
            </div>

            <LocationFormModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                mode={modalMode}
                initial={selected}
                onSave={handleSave}
            />

            <Modal
                isOpen={assignModalOpen && Boolean(assignLocation)}
                onClose={() => {
                    if (assignSubmitting) return;
                    setAssignModalOpen(false);
                    setAssignLocation(null);
                }}
                title={`Assign PSO — ${assignLocation?.name ?? ''}`}
                size="xl"
                closeOnBackdropClick={!assignSubmitting}
            >
                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Assign or unassign PSOs for branches in this location. This updates each branch’s <span className="font-mono">psoId</span>.
                    </p>

                    {/* Bulk assign PSO to region */}
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/40">
                        <label className="mb-2 block text-xs font-semibold text-gray-600 dark:text-gray-300">
                            Assign PSO to all branches in this region
                        </label>
                        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
                            <select
                                value={bulkAssignPsoId}
                                onChange={(e) => setBulkAssignPsoId(e.target.value)}
                                disabled={assignSubmitting || bulkSubmitting}
                                className="h-10 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                            >
                                <option value="">Select PSO…</option>
                                {psos
                                    .slice()
                                    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }))
                                    .map((p) => (
                                        <option key={p.id} value={(p as any).psoId || p.id}>
                                            {p.name || '—'}
                                        </option>
                                    ))}
                            </select>
                            <Button
                                type="button"
                                className="h-10 !py-0"
                                disabled={assignSubmitting || bulkSubmitting || !bulkAssignPsoId}
                                onClick={openBulkPreview}
                            >
                                Preview & Apply
                            </Button>
                        </div>
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            It will update only branches not already assigned to the selected PSO.
                        </p>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                PSO
                            </label>
                            <select
                                value={assignPsoFilter}
                                onChange={(e) => setAssignPsoFilter(e.target.value)}
                                disabled={assignSubmitting}
                                className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                            >
                                <option value="all">All</option>
                                <option value="unassigned">Unassigned</option>
                                {psosInSelectedLocation.map((p) => (
                                    <option key={p.id} value={p.value}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="relative flex-1 sm:max-w-md">
                            <Icon
                                icon={FiSearch}
                                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                            />
                            <input
                                type="search"
                                value={assignSearch}
                                onChange={(e) => setAssignSearch(e.target.value)}
                                placeholder="Search by branch code, name/address, or PSO…"
                                className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                disabled={assignSubmitting}
                            />
                        </div>
                    </div>

                    {branchesForLocation.length === 0 ? (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-300">
                            No branches found for this location.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredBranchesForAssignModal.map((b) => (
                                <div
                                    key={b.id}
                                    className="grid grid-cols-1 gap-2 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-black/30 sm:grid-cols-3 sm:items-center"
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                            {b.branchCode || '—'}
                                        </div>
                                        <div className="truncate text-xs text-gray-500 dark:text-gray-400" title={b.address}>
                                            {b.address || '—'}
                                        </div>
                                        <div className="mt-1">
                                            <span
                                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                                    b.isActive !== false
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                }`}
                                            >
                                                {b.isActive !== false ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                            <select
                                                value={assignmentDraft[b.id] ?? ''}
                                                onChange={(e) =>
                                                    setAssignmentDraft((s) => ({ ...s, [b.id]: e.target.value }))
                                                }
                                                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                                disabled={assignSubmitting}
                                            >
                                                <option value="">Unassigned</option>
                                                {psos.map((p) => (
                                                    <option key={p.id} value={(p as any).psoId || p.id}>
                                                        {p.name || '—'}
                                                    </option>
                                                ))}
                                            </select>

                                            <button
                                                type="button"
                                                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 disabled:pointer-events-none disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                                                disabled={assignSubmitting}
                                                onClick={() => setBranchActive(b, b.isActive === false)}
                                                title={b.isActive !== false ? 'Mark inactive' : 'Mark active'}
                                            >
                                                {b.isActive !== false ? 'Inactivate' : 'Activate'}
                                            </button>

                                            <select
                                                value={String(b.locationId || '')}
                                                onChange={(e) => shiftBranchRegion(b, e.target.value)}
                                                disabled={assignSubmitting}
                                                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 disabled:pointer-events-none disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-blue-400 dark:hover:bg-blue-900/20"
                                                title="Shift branch to another region"
                                            >
                                                {rows.map((loc) => (
                                                    <option key={loc.id} value={loc.id}>
                                                        {loc.name || '—'}
                                                    </option>
                                                ))}
                                            </select>

                                            <button
                                                type="button"
                                                className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-700 transition-all hover:border-red-400 hover:bg-red-50 disabled:pointer-events-none disabled:opacity-50 dark:border-red-900/50 dark:bg-gray-800 dark:text-red-400 dark:hover:border-red-500 dark:hover:bg-red-950/30"
                                                disabled={assignSubmitting}
                                                onClick={() => openDeleteBranch(b)}
                                                title="Delete branch permanently"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {filteredBranchesForAssignModal.length === 0 && (
                                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-300">
                                    No branches match these filters.
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={assignSubmitting}
                            onClick={() => {
                                setAssignModalOpen(false);
                                setAssignLocation(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button type="button" disabled={assignSubmitting} onClick={saveAssignments}>
                            {assignSubmitting ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={bulkPreviewOpen}
                onClose={() => {
                    if (bulkSubmitting) return;
                    setBulkPreviewOpen(false);
                }}
                title="Apply PSO assignment to region?"
                size="lg"
                closeOnBackdropClick={!bulkSubmitting}
            >
                <div className="space-y-4 p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        Total branches in this region: <strong>{(locationStats.allBranchesByLocation.get(assignLocation?.id || '') ?? []).length}</strong>
                        <br />
                        Branches that will be updated: <strong>{bulkBranchesToUpdate.length}</strong>
                    </p>

                    {bulkBranchesToUpdate.length > 0 ? (
                        <div className="max-h-64 overflow-auto rounded-lg border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-black/30">
                            <ul className="list-disc space-y-1 pl-5 text-gray-700 dark:text-gray-200">
                                {bulkBranchesToUpdate.map((b) => (
                                    <li key={b.id}>
                                        {b.branchCode || '—'}{' '}
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            ({b.psoName ? `from ${b.psoName}` : 'unassigned'})
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-300">
                            Nothing to update — all branches already assigned to this PSO.
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="secondary" disabled={bulkSubmitting} onClick={() => setBulkPreviewOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            disabled={bulkSubmitting || bulkBranchesToUpdate.length === 0}
                            onClick={confirmBulkAssign}
                        >
                            {bulkSubmitting ? 'Applying…' : 'Apply'}
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={branchDeleteOpen && Boolean(branchToDelete)}
                onClose={() => {
                    if (branchDeleteSubmitting) return;
                    setBranchDeleteOpen(false);
                    setBranchToDelete(null);
                }}
                title="Delete branch permanently?"
                size="sm"
                closeOnBackdropClick={!branchDeleteSubmitting}
            >
                <div className="space-y-4 p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        This will permanently delete branch{' '}
                        <strong className="text-gray-900 dark:text-gray-100">{branchToDelete?.branchCode ?? ''}</strong>. This cannot be undone.
                    </p>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={branchDeleteSubmitting}
                            onClick={() => {
                                setBranchDeleteOpen(false);
                                setBranchToDelete(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            className="bg-red-600 hover:bg-red-700"
                            disabled={branchDeleteSubmitting}
                            onClick={confirmDeleteBranch}
                        >
                            {branchDeleteSubmitting ? 'Deleting…' : 'Delete'}
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={deleteLocOpen && Boolean(locToDelete)}
                onClose={() => {
                    if (deleteLocSubmitting) return;
                    setDeleteLocOpen(false);
                    setLocToDelete(null);
                }}
                title="Delete location?"
                size="sm"
                closeOnBackdropClick={!deleteLocSubmitting}
            >
                <div className="space-y-4 p-6">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        This will permanently delete <strong className="text-gray-900 dark:text-gray-100">{locToDelete?.name ?? 'this location'}</strong>.
                        You can only delete a location when it has <strong>no branches</strong>.
                    </p>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={deleteLocSubmitting}
                            onClick={() => {
                                setDeleteLocOpen(false);
                                setLocToDelete(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            className="bg-red-600 hover:bg-red-700"
                            disabled={deleteLocSubmitting}
                            onClick={confirmDeleteLocation}
                        >
                            {deleteLocSubmitting ? 'Deleting…' : 'Delete'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast((s) => ({ ...s, show: false }))}
                />
            )}
        </>
    );
}
